import { useGetCmsTheme, useUpdateCmsTheme } from "@/api/cms";
import { CmsAppearanceHint } from "@/components/cms/theme/CmsAppearanceHint";
import { CmsTypographyPicker } from "@/components/cms/theme/CmsTypographyPicker";
import {
  ColorPaletteManager,
  type PaletteColorDraft,
} from "@/components/cms/theme/ColorPaletteManager";
import {
  HeaderShellThemeFields,
  type HeaderShellThemeValues,
} from "@/components/cms/theme/HeaderShellThemeFields";
import { PaletteColorPicker } from "@/components/cms/theme/PaletteColorPicker";
import {
  ShellThemeFields,
  type ShellThemeValues,
} from "@/components/cms/theme/ShellThemeFields";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/auth/AuthContext";
import { CmsThemeEditorProvider } from "@/context/cms/CmsThemeEditorContext";
import { useToast } from "@/hooks/use-toast";
import CMSLayout from "@/layout/CMSLayout";
import type { CmsTheme } from "@/lib/cms/cms-theme.constants";
import { cmsThemeSchema, type CmsThemeForm } from "@/lib/cms/cms-types";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

type ThemeFormState = {
  colors: PaletteColorDraft[];
  defaultPageBackgroundColorId: string | null;
  defaultPageTextColorId: string | null;
  defaultPageLinkColorId: string | null;
  defaultPageAltBackgroundColorId: string | null;
  defaultPageAltTextColorId: string | null;
  defaultPageFontFamily: string | null;
  defaultPageFontVariant: string | null;
  headerStyle: HeaderShellThemeValues;
  footerStyle: ShellThemeValues;
};

function themeToFormState(theme: CmsTheme): ThemeFormState {
  return {
    colors: theme.colors.map((c) => ({
      id: c.id,
      name: c.name,
      hex: c.hex,
      sortOrder: c.sortOrder,
    })),
    defaultPageBackgroundColorId: theme.defaultPageBackgroundColorId,
    defaultPageTextColorId: theme.defaultPageTextColorId,
    defaultPageLinkColorId: theme.defaultPageLinkColorId,
    defaultPageAltBackgroundColorId: theme.defaultPageAltBackgroundColorId,
    defaultPageAltTextColorId: theme.defaultPageAltTextColorId,
    defaultPageFontFamily: theme.defaultPageFontFamily,
    defaultPageFontVariant: theme.defaultPageFontVariant,
    headerStyle: {
      backgroundColorId: theme.headerConfig.backgroundColorId,
      textColorId: theme.headerConfig.textColorId,
      separatorType: theme.headerConfig.separatorType,
      separatorColorId: theme.headerConfig.separatorColorId,
      buttonColorId: theme.headerConfig.buttonColorId ?? null,
      fontFamily: theme.headerConfig.fontFamily,
      fontVariant: theme.headerConfig.fontVariant,
    },
    footerStyle: {
      backgroundColorId: theme.footerConfig.backgroundColorId,
      textColorId: theme.footerConfig.textColorId,
      separatorType: theme.footerConfig.separatorType,
      separatorColorId: theme.footerConfig.separatorColorId,
      fontFamily: theme.footerConfig.fontFamily,
      fontVariant: theme.footerConfig.fontVariant,
    },
  };
}

function sanitizeColorId(
  colorIds: Set<string>,
  colorId: string | null | undefined,
): string | null {
  return colorId && colorIds.has(colorId) ? colorId : null;
}

function sanitizeThemeStateAfterPaletteChange(
  state: ThemeFormState,
  nextColors: Array<{
    id: string;
    name: string;
    hex: string;
    sortOrder: number;
  }>,
): ThemeFormState {
  const colorIds = new Set(nextColors.map((color) => color.id));

  return {
    ...state,
    colors: nextColors.map((c, index) => ({
      id: c.id.startsWith("temp-") ? undefined : c.id,
      name: c.name,
      hex: c.hex,
      sortOrder: c.sortOrder ?? index,
    })),
    defaultPageBackgroundColorId: sanitizeColorId(
      colorIds,
      state.defaultPageBackgroundColorId,
    ),
    defaultPageTextColorId: sanitizeColorId(
      colorIds,
      state.defaultPageTextColorId,
    ),
    defaultPageLinkColorId: sanitizeColorId(
      colorIds,
      state.defaultPageLinkColorId,
    ),
    defaultPageAltBackgroundColorId: sanitizeColorId(
      colorIds,
      state.defaultPageAltBackgroundColorId,
    ),
    defaultPageAltTextColorId: sanitizeColorId(
      colorIds,
      state.defaultPageAltTextColorId,
    ),
    headerStyle: {
      ...state.headerStyle,
      backgroundColorId: sanitizeColorId(
        colorIds,
        state.headerStyle.backgroundColorId,
      ),
      textColorId: sanitizeColorId(colorIds, state.headerStyle.textColorId),
      separatorColorId: sanitizeColorId(
        colorIds,
        state.headerStyle.separatorColorId,
      ),
      buttonColorId: sanitizeColorId(colorIds, state.headerStyle.buttonColorId),
    },
    footerStyle: {
      ...state.footerStyle,
      backgroundColorId: sanitizeColorId(
        colorIds,
        state.footerStyle.backgroundColorId,
      ),
      textColorId: sanitizeColorId(colorIds, state.footerStyle.textColorId),
      separatorColorId: sanitizeColorId(
        colorIds,
        state.footerStyle.separatorColorId,
      ),
    },
  };
}

/**
 * Éditeur d'apparence (palette, défauts de page, header, footer, boutons) —
 * EXTRAIT de la page pour être réutilisé par l'interface WordPress-like du CMS
 * avancé (`/cms/advanced/apparence`). Rend le `<form id="cms-appearance">` :
 * l'hôte (CMSLayout ou CmsAdminShell) fournit le bouton Enregistrer qui
 * `requestSubmit()` ce formulaire.
 */
export function AppearanceEditor() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { instanceData } = useAuth();
  const { data: themeData } = useGetCmsTheme();
  const updateTheme = useUpdateCmsTheme();

  const [formState, setFormState] = useState<ThemeFormState>({
    colors: [],
    defaultPageBackgroundColorId: null,
    defaultPageTextColorId: null,
    defaultPageLinkColorId: null,
    defaultPageAltBackgroundColorId: null,
    defaultPageAltTextColorId: null,
    defaultPageFontFamily: null,
    defaultPageFontVariant: null,
    headerStyle: {
      backgroundColorId: null,
      textColorId: null,
      separatorType: "NONE",
      separatorColorId: null,
      buttonColorId: null,
      fontFamily: null,
      fontVariant: null,
    },
    footerStyle: {
      backgroundColorId: null,
      textColorId: null,
      separatorType: "SOLID",
      separatorColorId: null,
      fontFamily: null,
      fontVariant: null,
    },
  });

  useEffect(() => {
    if (themeData) setFormState(themeToFormState(themeData));
  }, [themeData]);

  const paletteForPickers = useMemo(
    () =>
      formState.colors.map((c, i) => ({
        id: c.id ?? `temp-${i}`,
        name: c.name,
        hex: c.hex,
        sortOrder: c.sortOrder,
      })),
    [formState.colors],
  );

  const handlePaletteColorsChange = (colors: typeof paletteForPickers) => {
    setFormState((s) => sanitizeThemeStateAfterPaletteChange(s, colors));
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!instanceData?.CMS?.id) return;

    const payload: CmsThemeForm & { id: string } = {
      id: instanceData.CMS.id,
      colors: formState.colors.map((c, index) => ({
        id: c.id,
        name: c.name,
        hex: c.hex,
        sortOrder: c.sortOrder ?? index,
      })),
      defaultPageBackgroundColorId: formState.defaultPageBackgroundColorId,
      defaultPageTextColorId: formState.defaultPageTextColorId,
      defaultPageLinkColorId: formState.defaultPageLinkColorId,
      defaultPageAltBackgroundColorId: formState.defaultPageAltBackgroundColorId,
      defaultPageAltTextColorId: formState.defaultPageAltTextColorId,
      defaultPageFontFamily: formState.defaultPageFontFamily,
      defaultPageFontVariant: formState.defaultPageFontVariant,
      header: {
        backgroundColorId: formState.headerStyle.backgroundColorId,
        textColorId: formState.headerStyle.textColorId,
        separatorType: formState.headerStyle.separatorType,
        separatorColorId: formState.headerStyle.separatorColorId,
        buttonColorId: formState.headerStyle.buttonColorId,
        fontFamily: formState.headerStyle.fontFamily,
        fontVariant: formState.headerStyle.fontVariant,
      },
      footer: {
        backgroundColorId: formState.footerStyle.backgroundColorId,
        textColorId: formState.footerStyle.textColorId,
        separatorType: formState.footerStyle.separatorType,
        separatorColorId: formState.footerStyle.separatorColorId,
        fontFamily: formState.footerStyle.fontFamily,
        fontVariant: formState.footerStyle.fontVariant,
      },
    };

    const parsed = cmsThemeSchema.safeParse(payload);
    if (!parsed.success) {
      toast({
        title: t("error"),
        description: t("cms.appearance.error.validation"),
        variant: "error",
      });
      return;
    }

    updateTheme.mutate(payload, {
      onSuccess: (data) => {
        setFormState(themeToFormState(data));
        toast({
          title: t("success"),
          description: t("cms.appearance.success"),
          variant: "success",
        });
      },
      onError: () => {
        toast({
          title: t("error"),
          description: t("cms.appearance.error.save"),
          variant: "error",
        });
      },
    });
  };

  return (
      <CmsThemeEditorProvider
        colors={paletteForPickers}
        onColorsChange={handlePaletteColorsChange}
      >
        <form id="cms-appearance" onSubmit={onSubmit} className="w-full">
          <Tabs defaultValue="palette" className="w-full">
            {/* Fond transparent à l'état actif (pas de blanc plaqué sur le
                fond gris clair de la page). */}
            <TabsList className="mb-6">
              <TabsTrigger
                value="palette"
                className="data-[state=active]:bg-transparent"
              >
                {t("cms.appearance.tabs.palette")}
              </TabsTrigger>
              <TabsTrigger
                value="page-default"
                className="data-[state=active]:bg-transparent"
              >
                {t("cms.appearance.tabs.pageDefault")}
              </TabsTrigger>
              <TabsTrigger
                value="header"
                className="data-[state=active]:bg-transparent"
              >
                {t("cms.appearance.tabs.header")}
              </TabsTrigger>
              <TabsTrigger
                value="footer"
                className="data-[state=active]:bg-transparent"
              >
                {t("cms.appearance.tabs.footer")}
              </TabsTrigger>
              <TabsTrigger
                value="buttons"
                className="data-[state=active]:bg-transparent"
              >
                {t("cms.appearance.tabs.button")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="palette">
              <ColorPaletteManager
                colors={formState.colors}
                onChange={(colors) => setFormState((s) => ({ ...s, colors }))}
              />
            </TabsContent>

            <TabsContent
              value="header"
              className="flex max-w-2xl flex-col gap-6"
            >
              <HeaderShellThemeFields
                colors={paletteForPickers}
                values={formState.headerStyle}
                onColorsChange={handlePaletteColorsChange}
                onChange={(headerStyle) =>
                  setFormState((s) => ({ ...s, headerStyle }))
                }
              />
            </TabsContent>

            <TabsContent
              value="footer"
              className="flex max-w-2xl flex-col gap-6"
            >
              <ShellThemeFields
                colors={paletteForPickers}
                values={formState.footerStyle}
                onColorsChange={handlePaletteColorsChange}
                onChange={(footerStyle) =>
                  setFormState((s) => ({ ...s, footerStyle }))
                }
              />
            </TabsContent>

            <TabsContent
              value="buttons"
              className="flex max-w-2xl flex-col gap-6"
            >
              <PaletteColorPicker
                label={t("cms.appearance.defaultButtonColor")}
                colors={paletteForPickers}
                value={formState.headerStyle.buttonColorId}
                onColorsChange={handlePaletteColorsChange}
                onChange={(buttonColorId) =>
                  setFormState((s) => ({
                    ...s,
                    headerStyle: { ...s.headerStyle, buttonColorId },
                  }))
                }
                noneLabel={t("cms.appearance.defaultButtonColorDefault")}
              />
            </TabsContent>

            <TabsContent
              value="page-default"
              className="flex max-w-2xl flex-col gap-6"
            >
              <CmsTypographyPicker
                label={t("cms.appearance.pageDefaultTypography")}
                description={t("cms.appearance.pageDefaultTypographyHint")}
                value={{
                  fontFamily: formState.defaultPageFontFamily,
                  fontVariant: formState.defaultPageFontVariant,
                }}
                onChange={({ fontFamily, fontVariant }) =>
                  setFormState((s) => ({
                    ...s,
                    defaultPageFontFamily: fontFamily,
                    defaultPageFontVariant: fontVariant,
                  }))
                }
              />
              <PaletteColorPicker
                label={t("cms.appearance.pageDefaultBackground")}
                colors={paletteForPickers}
                value={formState.defaultPageBackgroundColorId}
                onColorsChange={handlePaletteColorsChange}
                onChange={(defaultPageBackgroundColorId) =>
                  setFormState((s) => ({ ...s, defaultPageBackgroundColorId }))
                }
              />
              <PaletteColorPicker
                label={t("cms.appearance.pageDefaultText")}
                colors={paletteForPickers}
                value={formState.defaultPageTextColorId}
                onColorsChange={handlePaletteColorsChange}
                onChange={(defaultPageTextColorId) =>
                  setFormState((s) => ({ ...s, defaultPageTextColorId }))
                }
              />
              <PaletteColorPicker
                label={t(
                  "cms.appearance.pageDefaultLink",
                  "Couleur des liens (survol)",
                )}
                colors={paletteForPickers}
                value={formState.defaultPageLinkColorId}
                onColorsChange={handlePaletteColorsChange}
                onChange={(defaultPageLinkColorId) =>
                  setFormState((s) => ({ ...s, defaultPageLinkColorId }))
                }
              />
              <PaletteColorPicker
                label={t("cms.appearance.pageDefaultAltBackground")}
                colors={paletteForPickers}
                value={formState.defaultPageAltBackgroundColorId}
                onColorsChange={handlePaletteColorsChange}
                onChange={(defaultPageAltBackgroundColorId) =>
                  setFormState((s) => ({ ...s, defaultPageAltBackgroundColorId }))
                }
              />
              <PaletteColorPicker
                label={t("cms.appearance.pageDefaultAltText")}
                colors={paletteForPickers}
                value={formState.defaultPageAltTextColorId}
                onColorsChange={handlePaletteColorsChange}
                onChange={(defaultPageAltTextColorId) =>
                  setFormState((s) => ({ ...s, defaultPageAltTextColorId }))
                }
              />
              <CmsAppearanceHint>{t("cms.appearance.pageDefaultAltHint")}</CmsAppearanceHint>
              <CmsAppearanceHint>{t("cms.appearance.pageDefaultTextHint")}</CmsAppearanceHint>
            </TabsContent>
          </Tabs>
        </form>
      </CmsThemeEditorProvider>
  );
}

/** Page classique (`/cms/apparence`) : l'éditeur dans le layout de l'ancien CMS. */
export default function AppearanceCMS() {
  const { t } = useTranslation();
  return (
    <CMSLayout
      pageName={t("cms.appearance.title")}
      pageSubtitle={t("cms.appearance.subtitleHint")}
      formIds={["cms-appearance"]}
    >
      <AppearanceEditor />
    </CMSLayout>
  );
}
